import { createLikeableSchema, LikeableEntity } from 'mk2/containers/Like/Like.schemas';
import { Entity, PhotoEntity, PhotoSchema, UserEntity, UserSchema } from 'mk2/schemas';
import { schema } from 'normalizr';

export enum PostVisibility {
    Public = 0,
    Authenticated = 1,
    Friends = 2,
    Private = 3,
    Group = 4,
}

export interface ArticleEntity extends Entity {
    user: UserEntity;

    title: string;
    content: string;
    content2: string;
    slug: string;
    photos: PhotoEntity[];
    viewsCount: number;
    createdAt: string;
    likeable: LikeableEntity;
    visibility: PostVisibility;

    isPublished: boolean;
    isStaffpick: boolean;
    isPRArticle: boolean;
    isMagazine: boolean;
    isRedaction: boolean;
    isCommercial: boolean;
    isActiveAmbasador: boolean;
    isNoAds: boolean;
}

export const ArticleSchema = new schema.Entity('blogArticles', {
    user: UserSchema,
    photos: [PhotoSchema],
    likeable: createLikeableSchema('post'),
});

export interface CommentEntity extends Entity {
    id: number;
    text: string;
    authorUser: UserEntity;
    created: string;
    stickerId: number;
    postId: number;
    positionInPost: number;
    photos: PhotoEntity[];
    targetOwner: UserEntity;
    likeable: LikeableEntity;
    isLikeable: boolean;
    canDelete: boolean;
    deletedAt?: string;
    deletedByAuthor?: boolean;
    deletedReason?: string;
    deletedBy?: UserEntity;
}

export const CommentSchema = new schema.Entity('comments', {
    authorUser: UserSchema,
    targetOwner: UserSchema,
    photo: PhotoSchema,
    likeable: createLikeableSchema('comment'),
    deletedBy: UserSchema,
});

export interface ImageEntity extends Entity {
    postUrl: string;
    title: string;
    commentsCount: number;
    likesCount: number;
    photo: PhotoEntity;
    likeable: LikeableEntity;
}

export const ImageSchema = new schema.Entity('blogImages', {
    photo: PhotoSchema,
    likeable: createLikeableSchema('photoblog-image'),
});

export interface FacebookEngagementStats {
    reactionCount: number;
    commentCount: number;
    shareCount: number;
}

export interface GroupEntity extends Entity {
    categoryId: number;
    name: string;
    description: string;
    moderators: UserEntity[];
    coverPhoto: PhotoEntity;
    deletedAt: string;
    // deletedReason: string;
    // deletedBy: UserEntity;
    activeMembersCount: number;
    locked: boolean;
    imagesCount: number;
    membersCount: number;
    isDomainGroup: boolean;
    isListedPrivate: boolean;
}

export const GroupSchema = new schema.Entity('blogGroups', {
    moderators: [UserSchema],
    coverPhoto: PhotoSchema,
    deletedBy: UserSchema,
});

export enum GroupMemberRoles {
    MEMBER = 0,
    MODERATOR = 1,
}

export interface GroupMemberEntity extends Entity {
    user: UserEntity;
    group: GroupEntity;
    role: GroupMemberRoles;
    createdTime: string;
    blockedTime: string;
    blockedReason: string;
    blockedByUser: UserEntity;
}

export const GroupMemberSchema = new schema.Entity('blogGroupMembers', {
    user: UserSchema,
    group: GroupSchema,
    blockedByUser: UserSchema,
});

export enum GroupInvitationStatuses {
    PENDING = 0,
    ACCEPTED = 1,
    REFUSED = 2,
}

export interface GroupInvitationEntity extends Entity {
    user: UserEntity;
    group: GroupEntity;
    role: GroupMemberRoles;
    inviter: UserEntity;
    status: GroupInvitationStatuses;
    createdTime: string;
}

export const GroupInvitationSchema = new schema.Entity('blogGroupInvitations', {
    user: UserSchema,
    group: GroupSchema,
    inviter: UserSchema,
});

export interface BlogPostEntity extends Entity {
    group?: GroupEntity;
    author: UserEntity;
    created: string;
    heading: string;
    perex: string;
    images: ImageEntity[];
    stickerId: number;
    snippet: string;
    likeable: LikeableEntity;
    commentsCount: number;
    complaintsCount: number;
    sticky: boolean;
    lastMessageTime: string;
    slug: string;
    popularAddedOn: string;
    noindex: boolean;
    isNoAds: boolean;
    deletedAt?: string;
    deletedByAuthor?: boolean;
    deletedReason?: string;
    deletedBy?: UserEntity;
    wasEdited: boolean;
}

export const BlogPostSchema = new schema.Entity('blogPosts', {
    likeable: createLikeableSchema('post'),
    group: GroupSchema,
    author: UserSchema,
    images: [ImageSchema],
    deletedBy: UserSchema,
});
